import {DatePipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatDialog} from '@angular/material/dialog';
import {MatIconModule} from '@angular/material/icon';
import {ActivatedRoute, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import * as moment from 'moment-timezone';
import {Project} from 'src/app/core/model/project';
import {GetProjectsAction} from 'src/app/core/store/projects/projects.action';
import {selectAllProjects} from 'src/app/core/store/projects/projects.selector';
import {GetAllTrackersAction} from 'src/app/core/store/trackers/trackers.action';
import {TimezoneService} from 'src/app/services/timezone.service';
import {OphButtonModule} from 'src/app/shared/design/oph-button/oph-button.module';
import {OphLoadingModule} from 'src/app/shared/design/oph-loading/oph-loading.module';
import {ProjectsDialogComponent} from '../dialogs/projects-dialog/projects-dialog.component';
import {PROJECTS_DIALOG_CONFIG} from '../shared/projects-constants';
import {ProjectRowMenuComponent} from './project-row-menu/project-row-menu.component';
import {MobileService} from 'src/app/core/page/mobile.service';

export interface ProjectListItem extends Project {
  statusLabel: string;
  statusClass: string;
  datePrefix?: string;
  targetCount?: number;
  taskCount?: number;
  metricCount?: number;
}

@Component({
  selector: 'projects-list',
  standalone: true,
  imports: [OphButtonModule, OphLoadingModule, MatIconModule, DatePipe, ProjectRowMenuComponent],
  templateUrl: './projects-list.component.html',
  styleUrl: './projects-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsListComponent {
  private readonly mobileService = inject(MobileService);
  private readonly timezoneService = inject(TimezoneService);
  private dialog = inject(MatDialog);
  private store$ = inject(Store);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  projects = toSignal(this.store$.pipe(select(selectAllProjects(true))), {initialValue: null as Project[] | null});

  allProjects = computed<ProjectListItem[]>(() => {
    const projects = this.projects();
    if (!projects) return [];

    const now = new Date();

    const challenges = projects.filter(p => p.isChallenge).map(p => this.toChallengeListItem(p, now));

    const regularProjects = projects.filter(p => !p.isChallenge).map(p => this.toProjectListItem(p));

    return [...challenges, ...regularProjects];
  });

  private getComputedStatusLabel(p: Project): string {
    if (p.status === 'stopped') return 'Stopped';
    if (p.status === 'inactive' || !p.startDateTime || !p.endDateTime) return 'Draft';

    const now = moment().tz(this.timezoneService.getTimeZone()).startOf('day');
    const projectStart = moment(p.startDateTime).tz(this.timezoneService.getTimeZone());

    if (now.isBefore(projectStart)) return 'Scheduled';
    if (this.timezoneService.areDatesWithinRange(p.startDateTime, p.endDateTime)) return 'Active';
    return 'Expired';
  }

  private getComputedStatusClass(p: Project): string {
    if (p.status === 'stopped') return 'status-stopped';
    if (p.status === 'inactive' || !p.startDateTime || !p.endDateTime) return 'status-inactive';

    const now = moment().tz(this.timezoneService.getTimeZone()).startOf('day');
    const projectStart = moment(p.startDateTime).tz(this.timezoneService.getTimeZone());

    if (now.isBefore(projectStart)) return 'status-scheduled';
    if (this.timezoneService.areDatesWithinRange(p.startDateTime, p.endDateTime)) return 'status-active';
    return 'status-expired';
  }

  private toChallengeListItem(p: Project, now: Date): ProjectListItem {
    return {
      ...p,
      datePrefix: new Date(p.startDateTime) < now ? 'started' : 'starts',
      targetCount: this.getMetricCount(p),
      statusLabel: this.getComputedStatusLabel(p),
      statusClass: this.getComputedStatusClass(p),
    };
  }

  private getTaskCount(p: Project): number {
    let count = 0;
    for (const step of p.steps ?? []) {
      count += step.tasks?.length ?? 0;
    }
    return count;
  }

  private getMetricCount(p: Project): number {
    let count = 0;
    for (const step of p.steps ?? []) {
      for (const task of step.tasks ?? []) {
        for (const subtask of task.subtasks ?? []) {
          count += subtask.metricIds?.length ?? 0;
        }
      }
    }
    return count;
  }

  private toProjectListItem(p: Project): ProjectListItem {
    return {
      ...p,
      taskCount: this.getTaskCount(p),
      metricCount: this.getMetricCount(p),
      statusLabel: this.getComputedStatusLabel(p),
      statusClass: this.getComputedStatusClass(p),
    };
  }

  constructor() {
    this.store$.dispatch(new GetProjectsAction({}));
  }

  navigateTo(project: Project): void {
    if (project.isChallenge && project.status === 'inactive') {
      this.router.navigate(['challenge-builder', project._id, 'overview'], {relativeTo: this.route});
      return;
    }
    this.router.navigate([project._id], {relativeTo: this.route});
  }

  onCreate(_type?: string): void {
    const dialog = this.dialog.open(ProjectsDialogComponent, {
      autoFocus: this.mobileService.isMobilePlatform() ? false : undefined,
      ...PROJECTS_DIALOG_CONFIG,
    });
    dialog.afterClosed().subscribe(project => {
      if (project) {
        this.onActionSuccess(project);
      }
    });
  }

  private onActionSuccess(project: Project): void {
    this.store$.dispatch(new GetProjectsAction({}));
    this.store$.dispatch(new GetAllTrackersAction({params: {projectId: project._id, pageSize: 1000}}));
    sessionStorage.setItem('ophStoredProjectId', project._id);
    this.router.navigate([project._id], {relativeTo: this.route});
  }
}
